import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import DateRange, {
  StandardAndCustomRangeType,
} from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import { Modal } from "@mapmycustomers/ui";

import DateRangeSelector from "@app/scene/dashboard/components/Sidebar/components/DateRangeSelector";
import { exportDrillDownData, fetchDrillDownData } from "@app/scene/dashboard/store/actions";
import {
  getDrillDownData,
  getDrillDownViewState,
  getStackRankViewState,
  isDrillDownLoading,
} from "@app/scene/dashboard/store/selectors";
import DrillDownViewState from "@app/scene/dashboard/store/stackRank/DrillDownViewState";
import StackRankViewState from "@app/scene/dashboard/store/stackRank/StackRankViewState";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import formatDateRange from "@app/util/stackRank/formatDateRange";
import useHiddenChat from "@app/util/useHiddenChat";

import Actions from "./components/Actions";
import DataTable from "./components/DataTable";
import Filters from "./components/Filters";
import styles from "./DrillDownModal.module.scss";

export interface DrillDownModalProps {
  onHide: () => void;
}

interface Props extends DrillDownModalProps {
  data: LeaderboardItem[];
  drillDownViewState: DrillDownViewState;
  loading?: boolean;
  onExportDrillDownData: () => void;
  onFetchDrillDownData: (
    payload: Partial<{
      drillDownViewState: Partial<DrillDownViewState>;
      viewState: Partial<StackRankViewState>;
    }>
  ) => void;
  viewState: StackRankViewState;
}

const DrillDownModal: FC<Props> = ({
  data,
  drillDownViewState,
  loading,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  viewState,
}) => {
  const intl = useIntl();

  const analytics = useExtendedAnalytics("Full screen", useAnalytics());
  useHiddenChat();

  useEffect(() => {
    onFetchDrillDownData({});
  }, [onFetchDrillDownData]);

  const [query, setQuery] = useState("");
  const filteredData = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) {
      return data;
    }

    return data.filter(
      (item) =>
        item.user?.fullName?.toLowerCase().includes(q) ||
        item.user?.username.toLowerCase().includes(q) ||
        item.team?.name.toLowerCase().includes(q)
    );
  }, [data, query]);

  const handleDateRangeChange = useCallback(
    (payload: { dateRange: DateRange; dateRangeType: StandardAndCustomRangeType }) => {
      const { dateRange, dateRangeType } = payload;
      onFetchDrillDownData({ viewState: { dateRange, dateRangeType } });
    },
    [onFetchDrillDownData]
  );

  const handleTeamChange = useCallback(
    (teamId: Team["id"] | undefined) => {
      analytics.clicked(["Team pill"]);
      onFetchDrillDownData({ drillDownViewState: { grouping: "user", teamId } });
    },
    [onFetchDrillDownData, analytics]
  );

  const handleGroupingChange = useCallback(
    (grouping: "team" | "user") => {
      analytics.clicked([`${grouping === "user" ? "Reps" : "Team"} icon`]);
      onFetchDrillDownData({ drillDownViewState: { grouping, teamId: undefined } });
    },
    [onFetchDrillDownData, analytics]
  );

  return (
    <Modal
      actions={
        <Actions
          onExport={onExportDrillDownData}
          onQueryChange={setQuery}
          query={query}
          viewState={viewState}
        />
      }
      className={styles.modal}
      footer={null}
      onCancel={onHide}
      open
      scrollable
      subtitle={
        <DateRangeSelector
          dateRange={viewState.dateRange}
          dateRangeType={viewState.dateRangeType}
          onChange={handleDateRangeChange}
        >
          <span>
            {formatDateRange(viewState.dateRange, "PPPP")}{" "}
            <FontAwesomeIcon className={styles.changeDateRange} icon={faCalendarAlt} />
          </span>
        </DateRangeSelector>
      }
      title={intl.formatMessage({
        id: "dashboard.stackRankDrillDown.title",
        defaultMessage: "Stack Rank",
        description: "Title of the drill down modal for the Stack Rank on the Dashboard page",
      })}
    >
      <Filters
        drillDownViewState={drillDownViewState}
        onGroupingChange={handleGroupingChange}
        onTeamChange={handleTeamChange}
      />
      <DataTable
        className={styles.table}
        data={filteredData}
        drillDownViewState={drillDownViewState}
        loading={loading}
        onTeamClick={handleTeamChange}
        rankingMetric={viewState.rankingMetric}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: getDrillDownData(state),
  drillDownViewState: getDrillDownViewState(state),
  loading: isDrillDownLoading(state),
  viewState: getStackRankViewState(state),
});

const mapDispatchToProps = {
  onExportDrillDownData: exportDrillDownData,
  onFetchDrillDownData: fetchDrillDownData.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDownModal);
